'use client';

import { useState, useMemo, type ReactNode } from 'react';
import { cn } from './cn';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { Skeleton } from './Skeleton';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Column<T> {
  /** Unique key — used for sorting and as React key */
  key: string;
  /** Column header label */
  header: string;
  /** Custom header render — overrides header string when provided */
  headerRender?: () => ReactNode;
  /** Render the cell content */
  render: (row: T) => ReactNode;
  /** Optional sort value extractor — if provided the column is sortable */
  sortValue?: (row: T) => string | number | null;
  /** Tailwind width class (e.g. 'w-24', 'flex-1', 'min-w-[200px]') */
  className?: string;
  /** Header alignment */
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  /** Unique key extractor */
  rowKey: (row: T) => string;
  /** Loading state */
  isLoading?: boolean;
  /** Skeleton row count during loading */
  skeletonRows?: number;
  /** Empty state icon + message */
  emptyIcon?: ReactNode;
  emptyMessage?: string;
  /** Optional secondary empty message */
  emptySubMessage?: string;
  /** Server-side pagination — pass these to enable */
  pagination?: {
    page: number;
    totalPages: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Dynamic row className */
  rowClassName?: (row: T) => string;
  /** Content rendered above the header (e.g. bulk actions bar) */
  topContent?: ReactNode;
  /** Extra className for the wrapper */
  className?: string;
}

type SortDir = 'asc' | 'desc';

// ── Component ────────────────────────────────────────────────────────────────

export function DataTable<T>({
  columns,
  data,
  rowKey,
  isLoading = false,
  skeletonRows = 8,
  emptyIcon,
  emptyMessage = 'Tidak ada data',
  emptySubMessage,
  pagination,
  onRowClick,
  rowClassName,
  topContent,
  className,
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (col: Column<T>) => {
    if (!col.sortValue) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(col.key);
      setSortDir('asc');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortValue) return data;
    const extract = col.sortValue;
    return [...data].sort((a, b) => {
      const va = extract(a);
      const vb = extract(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir, columns]);

  // ── Loading skeleton ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={cn('bg-white rounded-xl border border-slate-200 overflow-hidden', className)}>
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center gap-4">
          {columns.map((col) => (
            <Skeleton key={col.key} className={cn('h-3 w-20 rounded', col.className)} />
          ))}
        </div>
        {Array.from({ length: skeletonRows }).map((_, i) => (
          <div
            key={i}
            className="px-4 py-3 flex items-center gap-4 border-b border-slate-100 last:border-0"
          >
            {columns.map((col) => (
              <Skeleton key={col.key} className={cn('h-3.5 w-24 rounded', col.className)} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────────
  if (data.length === 0) {
    return (
      <div className={cn('bg-white rounded-xl border border-slate-200 overflow-hidden', className)}>
        <div className="text-center py-20 text-slate-400">
          {emptyIcon && <div className="mx-auto mb-3 opacity-50 [&>svg]:w-10 [&>svg]:h-10 [&>svg]:mx-auto">{emptyIcon}</div>}
          <p className="text-sm font-medium text-slate-500">{emptyMessage}</p>
          {emptySubMessage && <p className="text-xs text-slate-400 mt-1">{emptySubMessage}</p>}
        </div>
      </div>
    );
  }

  // ── Table ────────────────────────────────────────────────────────────────
  return (
    <div className={cn('bg-white rounded-xl border border-slate-200 overflow-hidden', className)}>
      {/* Top content (bulk actions, etc.) */}
      {topContent}

      {/* Header — table only. Below md the rows stack into labelled cards, so a
          shared header row would have nothing to align to. */}
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 hidden md:flex items-center gap-4">
        {columns.map((col) => {
          // Custom header render
          if (col.headerRender) {
            return (
              <div key={col.key} className={cn('min-w-0', col.className, col.headerClassName)}>
                {col.headerRender()}
              </div>
            );
          }

          const sortable = !!col.sortValue;
          const active = sortKey === col.key;
          return (
            <button
              key={col.key}
              type="button"
              disabled={!sortable}
              onClick={() => handleSort(col)}
              className={cn(
                'text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1 select-none',
                sortable && 'hover:text-slate-700 cursor-pointer',
                !sortable && 'cursor-default',
                col.className,
                col.headerClassName
              )}
            >
              {col.header}
              {sortable && (
                active ? (
                  sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronsUpDown className="w-3 h-3 opacity-40" />
                )
              )}
            </button>
          );
        })}
      </div>

      {/* Rows */}
      <div className="divide-y divide-slate-100">
        {sortedData.map((row) => (
          <div
            key={rowKey(row)}
            className={cn(
              'transition-colors',
              onRowClick && 'hover:bg-slate-50 cursor-pointer',
              rowClassName?.(row)
            )}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {/* ONE DOM tree at every breakpoint — each cell is rendered exactly once.
                Rendering a separate mobile card tree alongside the table (hidden via
                CSS) duplicates every cell in the accessibility tree: screen readers
                read each row twice, and getByText finds two of everything. Layout is
                CSS-only instead: column below md, row at md+.

                max-md:!w-full neutralizes the fixed column widths callers pass in
                col.className (w-40, w-28 …) — those are exactly what collide on a
                narrow viewport. They still apply at md+. */}
            <div className="px-4 py-3 flex flex-col gap-2 md:flex-row md:items-center md:gap-4">
              {columns.map((col) => (
                <div
                  key={col.key}
                  className={cn(
                    'min-w-0 flex items-baseline justify-between gap-3 max-md:!w-full md:block',
                    col.className
                  )}
                >
                  {/* Row label, mobile only — at md+ the sticky header supplies it. */}
                  {col.header ? (
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400 md:hidden">
                      {col.header}
                    </span>
                  ) : null}
                  <div className="min-w-0 break-words text-right md:text-left">
                    {col.render(row)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            Halaman {pagination.page} dari {pagination.totalPages} ({pagination.total} item)
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => pagination.onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => pagination.onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
